import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";
import { useUserNames } from "@/lib/useUserNames";

/**
 * Pharmacy payment receipt — A5 landscape.
 * Route: /print/pharm-receipt/:saleId/:idx
 * idx is the 0-based index of the payment in sale.payments[].
 */
export default function PrintPharmacyReceipt() {
  const { saleId, idx } = useParams();
  const [s, setS] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const letterhead = useLetterheadMode();
  const nameOf = useUserNames();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/pharmacy/sales/${saleId}`);
      setS(data);
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      const auto = new URLSearchParams(window.location.search).get("autoprint");
      if (auto !== "0") setTimeout(() => window.print(), 600);
    })();
  }, [saleId]);

  if (!s) return <div className="p-10 text-sm text-ink-muted">Loading…</div>;
  const i = parseInt(idx, 10) || 0;
  const p = (s.payments || [])[i];
  if (!p) return <div className="p-10 text-sm text-rose-700">Payment not found.</div>;

  const inWords = (n) => {
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
                  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (x) => x < 20 ? ones[x] : (tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : ""));
    const three = (x) => {
      const h = Math.floor(x / 100), rest = x % 100;
      return (h ? ones[h] + " Hundred" + (rest ? " " : "") : "") + (rest ? two(rest) : "");
    };
    const rupees = Math.floor(n);
    if (rupees === 0) return "Zero";
    const crore = Math.floor(rupees / 10000000);
    const lakh  = Math.floor((rupees % 10000000) / 100000);
    const thou  = Math.floor((rupees % 100000) / 1000);
    const rest  = rupees % 1000;
    return [
      crore && two(crore) + " Crore",
      lakh  && two(lakh)  + " Lakh",
      thou  && two(thou)  + " Thousand",
      rest  && three(rest),
    ].filter(Boolean).join(" ").trim();
  };

  const receiptNo = p.receipt_no || s.invoice_no || "";
  const receiptType = p.receipt_type || "Pharmacy";

  return (
    <div className="pharm-rcp mx-auto bg-white p-6 text-sm text-ink" data-testid="print-pharm-receipt">
      <style>{`
        @page { size: A5 landscape; margin: 8mm; }
        .pharm-rcp { width: 210mm; max-width: 210mm; min-height: 148mm; }
        @media print {
          html, body { width: 210mm; height: 148mm; margin: 0 !important; padding: 0 !important; background: white; }
          .pharm-rcp { width: 210mm !important; height: 148mm !important; box-shadow: none !important; }
        }
      `}</style>

      <LetterheadToggle />
      {!letterhead && (
        <PrintHeader
          compact
          hospital={hospital}
          branch={branch}
          subtitle={`${receiptType} · Payment Receipt`}
          right={<>
            <div>Receipt: <span className="font-mono font-semibold">{receiptNo}</span></div>
            <div>Date: {(p.received_at || "").slice(0, 16).replace("T", " ")}</div>
          </>}
        />
      )}

      <div className="mt-3 rounded border border-border bg-secondary/30 p-3">
        <div className="grid grid-cols-3 gap-3 text-[11px]">
          <div><div className="text-[9px] uppercase text-ink-muted">Received from</div><div className="text-sm font-semibold">{s.patient_name}</div></div>
          {s.patient_uhid && <div><div className="text-[9px] uppercase text-ink-muted">UHID</div><div className="font-mono">{s.patient_uhid}</div></div>}
          <div><div className="text-[9px] uppercase text-ink-muted">Against invoice</div><div className="font-mono">{s.invoice_no}</div></div>
          <div><div className="text-[9px] uppercase text-ink-muted">Payment mode</div><div className="capitalize">{(p.mode || "").replace("_", " ")}</div></div>
          {p.reference && <div><div className="text-[9px] uppercase text-ink-muted">Reference</div><div className="font-mono">{p.reference}</div></div>}
          {p.received_by && <div><div className="text-[9px] uppercase text-ink-muted">Received by</div><div>{nameOf(p.received_by, p.received_by)}</div></div>}
        </div>
      </div>

      <div className="mt-3 rounded border-2 border-brand p-3 text-center">
        <div className="text-[10px] uppercase tracking-wider text-ink-muted">Amount received</div>
        <div className="mt-1 font-heading text-3xl font-bold text-brand">₹ {Number(p.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
        <div className="mt-1 text-[11px] italic text-ink-muted">Rupees {inWords(p.amount)} Only</div>
      </div>

      {/* Running totals — invoice context so the customer sees what's left */}
      <div className="mt-3 grid grid-cols-3 gap-2 rounded border border-border bg-secondary/30 p-2 text-[11px]">
        <div className="text-center"><div className="text-[9px] uppercase text-ink-muted">Invoice total</div><div className="font-semibold">₹ {Number(s.total || 0).toLocaleString("en-IN")}</div></div>
        <div className="text-center"><div className="text-[9px] uppercase text-ink-muted">Paid so far</div><div className="font-semibold text-emerald-700">₹ {Number(s.paid_amount || 0).toLocaleString("en-IN")}</div></div>
        <div className="text-center"><div className="text-[9px] uppercase text-ink-muted">Outstanding</div><div className={`font-semibold ${Number(s.balance || 0) > 0 ? "text-rose-700" : "text-emerald-700"}`}>₹ {Number(s.balance || 0).toLocaleString("en-IN")}</div></div>
      </div>

      {p.notes && <div className="mt-2 text-[11px] text-ink-muted"><b>Notes:</b> {p.notes}</div>}

      <div className="mt-6 flex justify-between text-[10px] text-ink-muted">
        <div>Patient / Attendant signature</div>
        <div>For {hospital?.name || BRAND.name} · Pharmacist / Cashier</div>
      </div>

      <div className="mt-4 border-t border-border pt-2 text-center text-[10px] text-ink-muted">{BRAND.footer}</div>
    </div>
  );
}
